import {
  type StaffCatalogLocationAssessment,
  type StaffCatalogLocationSource,
  type StaffCatalogPlaceMatchState,
  type StaffCatalogPlaceReasonCode,
  type StaffCatalogPlacesErrorCode,
  staffCatalogLocationSources,
  type staffCatalogPlaceLookupKinds,
  type staffCatalogPlaceLookupOutcomes,
  type staffCatalogPlaceMatchDecisions,
  staffCatalogPlaceReasonCodes,
  staffCatalogPlaceReviewRefreshStates,
  staffCatalogPlaceReviewStates,
} from "@lafayette/schemas";

import { type Option, optionsOf } from "../labels";

// Plain-language labels for every code the places contract sends. Each table is a `Record` over the
// contract's tuple, so a new code in the contract fails the type check here until it has a label. No label
// holds provider content: provider names, addresses and coordinates never pass through this file.

export const matchStateLabels: Readonly<Record<StaffCatalogPlaceMatchState, string>> = {
  CANDIDATES_FOUND: "Ambiguous",
  NO_MATCH: "No match",
  MATCHED: "Matched",
  NO_PLACE: "No place",
  NEEDS_REVIEW: "Needs review",
  UNMATCHED: "Match cleared",
};

export const reasonLabels: Readonly<Record<StaffCatalogPlaceReasonCode, string>> = {
  WRONG_MATCH: "Wrong place",
  PLACE_CLOSED: "Place closed",
  PLACE_MOVED: "Place moved",
  NOT_A_PLACE: "Not a physical place",
  OTHER: "Other reason",
};
export const reasonOptions = optionsOf(staffCatalogPlaceReasonCodes, reasonLabels);

/** What a failed provider call means, as the end of a sentence ("Error: the place service did not answer"). */
export const placesErrorSentences: Readonly<Record<StaffCatalogPlacesErrorCode, string>> = {
  TIMEOUT: "the place service did not answer in time",
  RATE_LIMITED: "too many requests went to the place service",
  QUOTA_EXHAUSTED: "the daily limit of place lookups is used up",
  NOT_FOUND: "the place id no longer exists",
  INVALID_REQUEST: "the place service refused the request",
  PERMISSION_DENIED: "the place service key or billing needs attention",
  UNAVAILABLE: "the place service is not available",
};

export const decisionLabels: Readonly<
  Record<(typeof staffCatalogPlaceMatchDecisions)[number], string>
> = {
  SELECTED: "Matched",
  REPLACED: "Match corrected",
  CLEARED: "Match cleared",
  NO_PLACE: "Marked as no physical place",
};

export const lookupKindLabels: Readonly<
  Record<(typeof staffCatalogPlaceLookupKinds)[number], string>
> = {
  SEARCH_TEXT: "Search",
  GET_DETAILS: "Verification",
};

export const lookupOutcomeLabels: Readonly<
  Record<(typeof staffCatalogPlaceLookupOutcomes)[number], string>
> = {
  OK: "Answered",
  NO_MATCH: "No result",
  NOT_FOUND: "Place id not found",
  ERROR: "Failed",
};

export const locationSourceLabels: Readonly<Record<StaffCatalogLocationSource, string>> = {
  SITE_VISIT: "A site visit",
  VENUE_PUBLISHED: "The venue's own published details",
  SUPPLIER_PROVIDED: "A supplier",
  LICENSED_DATASET: "A licensed dataset",
  IMPORT_FILE: "An import file",
  NONE: "No source (everything unknown)",
};

/** The sources a staff member can choose. `NONE` is not a choice: it is what an all-unknown location sends. */
export const locationSourceOptions: readonly Option<StaffCatalogLocationSource>[] = optionsOf(
  staffCatalogLocationSources.filter((source) => source !== "NONE"),
  locationSourceLabels,
);

export const assessmentLabels: Readonly<Record<StaffCatalogLocationAssessment, string>> = {
  CONFIRMED: "Confirmed",
  UNCONFIRMED: "Unconfirmed",
  UNKNOWN: "Unknown",
  NOT_ASSESSED: "Not assessed",
};

export const assessmentExplanations: Readonly<Record<StaffCatalogLocationAssessment, string>> = {
  CONFIRMED: "A staff member confirmed this value from a stated source.",
  UNCONFIRMED: "There is a value, but nobody confirmed it, or a later edit changed it.",
  UNKNOWN: "A staff member recorded that this is not known.",
  NOT_ASSESSED: "The value is empty and nobody has looked at it yet.",
};

const reviewStateLabels: Readonly<Record<(typeof staffCatalogPlaceReviewStates)[number], string>> =
  {
    CANDIDATES_FOUND: "Ambiguous",
    NO_MATCH: "No match",
    NEEDS_REVIEW: "Needs review",
    UNMATCHED: "Match cleared",
  };
export const reviewStateOptions = optionsOf(staffCatalogPlaceReviewStates, reviewStateLabels);

const reviewRefreshLabels: Readonly<
  Record<(typeof staffCatalogPlaceReviewRefreshStates)[number], string>
> = {
  STALE: "Stale",
  ERROR: "Error",
};
export const reviewRefreshOptions = optionsOf(
  staffCatalogPlaceReviewRefreshStates,
  reviewRefreshLabels,
);

export const reviewLocationOptions: readonly Option<"NOT_ASSESSED" | "UNCONFIRMED">[] = [
  { value: "NOT_ASSESSED", label: "Location not assessed" },
  { value: "UNCONFIRMED", label: "Location unconfirmed" },
];
